import type {
  CreateTimeCategory,
  TimeCategory,
  UpdateTimeCategory,
} from '@scrambl/shared'

import { apiClient } from './api-client'
import { createSingletonApi } from './singleton-factory'

const BASE_ENDPOINT = '/time-categories'

/** Accepts both bare payloads and the `{ data }` envelope. */
function unwrap<T>(response: T | { data: T }): T {
  if (
    response &&
    typeof response === 'object' &&
    !Array.isArray(response) &&
    'data' in response
  ) {
    return (response as { data: T }).data
  }
  return response as T
}

/**
 * Time categories classify the kind of work behind an entry. Any member can
 * read them; creating, editing and reordering need time settings access.
 * There is no delete — archive with `isActive: false`.
 */
class TimeCategoryApiService {
  async getAll(
    options: { includeInactive?: boolean } = {},
  ): Promise<TimeCategory[]> {
    const query = options.includeInactive ? '?includeInactive=true' : ''
    const response = await apiClient<TimeCategory[] | { data: TimeCategory[] }>(
      `${BASE_ENDPOINT}${query}`,
    )
    const categories = unwrap(response)
    return Array.isArray(categories) ? categories : []
  }

  async create(data: CreateTimeCategory): Promise<TimeCategory> {
    const response = await apiClient<
      TimeCategory | { data: TimeCategory },
      CreateTimeCategory
    >(BASE_ENDPOINT, { method: 'POST', body: data })
    return unwrap(response)
  }

  async update(id: string, data: UpdateTimeCategory): Promise<TimeCategory> {
    const response = await apiClient<
      TimeCategory | { data: TimeCategory },
      UpdateTimeCategory
    >(`${BASE_ENDPOINT}/${id}`, { method: 'PATCH', body: data })
    return unwrap(response)
  }

  async reorder(ids: string[]): Promise<TimeCategory[]> {
    const response = await apiClient<
      TimeCategory[] | { data: TimeCategory[] },
      { ids: string[] }
    >(`${BASE_ENDPOINT}/reorder`, { method: 'PATCH', body: { ids } })
    const categories = unwrap(response)
    return Array.isArray(categories) ? categories : []
  }
}

export const timeCategoryApi = createSingletonApi(TimeCategoryApiService)
